import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { message } from 'antd';
import axios from 'axios';
import { useAuth } from '../auth.js';
import { CONSTRUCTION_TYPES_CA } from '../constants/constructionTypes_CA';
import { CONSTRUCTION_TYPES_US } from '../constants/constructionTypes_US';
import { OCCUPANCY_TYPES_CA } from '../constants/occupancyTypes_CA';
import { OCCUPANCY_TYPES_US } from '../constants/occupancyTypes_US';
import { phVerificationModalOpened, phExtractionApproved, phDocumentReclassified, phDocumentClassificationFailed } from '../utils/posthogEvents';
import {
  DELIVERABLE_DOC_CLASS_OPTIONS,
  DOC_CLASS_LABELS,
  inferDocClassFromFileName,
} from '../constants/docClasses.js';
import { loadProjectEntities } from '../utils/projectEntityHelpers';
import { formatAppliesToLabel } from '../utils/docAppliesToEntity';
import { uploadPollErrorMessage } from '../utils/sharepointFolderUpload.js';

const API_BASE_CLASSIFIER = process.env.REACT_APP_API_BASE_CLASSIFIER || 'http://localhost:8000';

const COMPLIANCE_TYPES = [
  { value: 'BUILDING_CODE',       label: 'Building Code' },
  { value: 'CLIENT_REQUIREMENTS', label: 'Client Requirements' },
  { value: 'ZONING',              label: 'Zoning' },
  { value: 'STANDARDS',           label: 'Standards' },
];


const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(24px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}`;
const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;


const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(3px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  width: 100%;
  max-width: 680px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.25s ease;
  font-family: 'Montserrat', system-ui, sans-serif;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.75rem 1rem;
  border-bottom: 1px solid rgba(0,0,0,0.07);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ModalTitleGroup = styled.div``;

const ModalTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #111;
  margin: 0 0 0.2rem;
`;

const ModalSubtitle = styled.p`
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(0,0,0,0.05);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  flex-shrink: 0;
  transition: all 0.2s;
  &:hover { background: rgba(0,0,0,0.1); color: #111; }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 1.25rem 1.75rem;
`;

const ScanInfo = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-size: 0.82rem;
  flex-wrap: wrap;
`;

const ScanInfoItem = styled.div`
  display: flex;
  gap: 0.3rem;
`;
const ScanLabel = styled.span`color: #9ca3af;`;
const ScanValue = styled.span`color: #111; font-weight: 600;`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  gap: 1rem;
  color: #6b7280;
  font-size: 0.85rem;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(233,112,36,0.2);
  border-top-color: #e97024;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const DocGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const DocGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
`;

const DocGroupTitle = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p =>
    p.$type === 'Requirement' ? '#15803d' :
    p.$type === 'Deliverable' ? '#2563eb' :
    '#d97706'};
`;

const DocGroupNote = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 400;
`;

const DocGroupCount = styled.span`
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.1rem 0.4rem;
  border-radius: 10px;
  background: ${p =>
    p.$type === 'Requirement' ? 'rgba(34,197,94,0.12)' :
    p.$type === 'Deliverable' ? 'rgba(59,130,246,0.12)' :
    'rgba(245,158,11,0.12)'};
  color: ${p =>
    p.$type === 'Requirement' ? '#15803d' :
    p.$type === 'Deliverable' ? '#2563eb' :
    '#d97706'};
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(0,0,0,0.07);
  margin-bottom: 0.5rem;
`;

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${p => p.$unknown ? 'rgba(245,158,11,0.3)' : 'rgba(0,0,0,0.07)'};
  background: ${p => p.$unknown ? 'rgba(245,158,11,0.04)' : '#fff'};
  margin-bottom: 0.375rem;
  transition: all 0.15s;
  &:hover { background: #f9fafb; }
`;

const DocIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 700;
  flex-shrink: 0;
  background: rgba(239,68,68,0.1);
  color: #dc2626;
`;

const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocName = styled.div`
  font-size: 0.845rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategorySelect = styled.select`
  padding: 0.3rem 0.625rem;
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.12);
  background: #f9fafb;
  font-size: 0.8rem;
  font-family: inherit;
  color: #333;
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  min-width: 130px;
  transition: border-color 0.2s;
  &:focus { border-color: #e97024; box-shadow: 0 0 0 2px rgba(233,112,36,0.12); }
`;

const AppliesToBox = styled.div`
  flex-shrink: 0;
  min-width: 140px;
  max-width: 200px;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 6px;
  background: #f9fafb;
  padding: 0.35rem 0.5rem;
  font-size: 0.75rem;
`;

const AppliesToSummary = styled.div`
  color: #555;
  font-weight: 600;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
`;

const AppliesToClear = styled.button`
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
  line-height: 1;
  &:hover { color: #e97024; }
`;

const AppliesToChecks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  max-height: 5.5rem;
  overflow-y: auto;
  label {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #444;
    cursor: pointer;
    font-weight: 400;
  }
  input { accent-color: #e97024; }
`;

const ViewToggleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const ViewToggle = styled.div`
  display: inline-flex;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  overflow: hidden;
`;

const ViewToggleBtn = styled.button`
  padding: 0.35rem 0.75rem;
  border: none;
  background: ${p => p.$active ? '#e97024' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#555'};
  font-size: 0.78rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
`;

const SummaryGroupCard = styled.div`
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 10px;
  padding: 0.875rem 1rem;
  margin-bottom: 0.625rem;
  background: #fff;
`;

const SummaryGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SummaryGroupCount = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111;
  min-width: 2.5rem;
`;

const SummaryGroupMeta = styled.div`
  flex: 1;
  min-width: 140px;
`;

const SummaryGroupTitle = styled.div`
  font-size: 0.88rem;
  font-weight: 600;
  color: #374151;
`;

const SummaryGroupActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const ExpandLink = styled.button`
  border: none;
  background: none;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  &:hover { text-decoration: underline; }
`;

const ExpandedFileList = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0,0,0,0.06);
  max-height: 160px;
  overflow-y: auto;
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.6;
`;

const ClassifyingBadge = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  font-style: italic;
  flex-shrink: 0;
  min-width: 130px;
  text-align: right;
`;

const ProcessingEstimate = styled.div`
  background: rgba(59,130,246,0.06);
  border: 1px solid rgba(59,130,246,0.15);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.82rem;
  color: #6b7280;
`;

const ErrorBanner = styled.div`
  background: rgba(220,38,38,0.06);
  border: 1px solid rgba(220,38,38,0.18);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.82rem;
  color: #991b1b;
  line-height: 1.45;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.75rem 1.5rem;
  border-top: 1px solid rgba(0,0,0,0.07);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const FooterLeft = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
`;

const FooterRight = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const SecondaryBtn = styled.button`
  padding: 0.5rem 1.1rem;
  border-radius: 8px;
  border: 1px solid rgba(0,0,0,0.1);
  background: rgba(0,0,0,0.05);
  font-size: 0.85rem;
  font-family: inherit;
  font-weight: 500;
  color: #444;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: rgba(0,0,0,0.1); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryBtn = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #e97024, #ff8c42);
  font-size: 0.85rem;
  font-family: inherit;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(233,112,36,0.3);
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(233,112,36,0.4); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const WizardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.42);
  backdrop-filter: blur(2px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  padding: 1.5rem;
`;

const WizardModal = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  width: 100%;
  max-width: 520px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', 'Montserrat', system-ui, sans-serif;
`;

const WizardHeader = styled.div`
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-shrink: 0;
`;

const WizardHeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
`;

const WizardIconRing = styled.div`
  width: 32px;
  height: 32px;
  background: rgba(233,112,36,0.07);
  border: 1px solid rgba(233,112,36,0.25);
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e97024;
  flex-shrink: 0;
`;

const WizardTitle = styled.h3`
  font-size: 0.97rem;
  font-weight: 700;
  color: #333;
  margin: 0;
  line-height: 1.2;
`;

const WizardSubtitle = styled.p`
  font-size: 0.75rem;
  color: #999;
  margin: 3px 0 0;
  line-height: 1.5;
`;

const WizardBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
`;

const WizardSectionLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #999;
  margin-bottom: 0.5rem;
`;

const ParamGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const ParamRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.75rem;
  background: #fafafa;
  border-radius: 4px;
  gap: 0.75rem;
`;

const ParamLabel = styled.span`
  font-size: 0.78rem;
  color: #555;
  font-weight: 500;
`;

const ParamValue = styled.span`
  font-size: 0.82rem;
  color: #333;
  font-weight: 600;
  text-align: right;
`;

const MissingValue = styled.span`
  font-size: 0.78rem;
  color: #d97706;
  font-style: italic;
  font-weight: 400;
`;

const MissingParamsNote = styled.div`
  margin-top: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #92400e;
  line-height: 1.5;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  margin-bottom: 0.4rem;
`;

const FieldHint = styled.div`
  font-size: 0.72rem;
  color: #999;
  margin-bottom: 0.375rem;
  line-height: 1.4;
`;

const WizardSelect = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-family: 'Poppins', 'Montserrat', system-ui, sans-serif;
  font-size: 0.88rem;
  color: #333;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition: all 0.15s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.875rem center;

  &:hover { background-color: #ebebeb; }
  &:focus {
    background-color: #fff;
    box-shadow: 0 0 0 2px rgba(233,112,36,0.22);
  }
`;

const WizardTypeDesc = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f5f5f5;
  border-left: 3px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.78rem;
  color: #555;
  line-height: 1.5;
`;

const WizardFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const FooterHint = styled.span`
  font-size: 0.72rem;
  color: #bbb;
  font-family: 'Poppins', 'Montserrat', system-ui, sans-serif;
`;

const WizardBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: 'Poppins', 'Montserrat', system-ui, sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 7px;
  padding: 0.55rem 1.25rem;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  white-space: nowrap;
`;

const WizardPrimaryBtn = styled(WizardBtn)`
  background: #e97024;
  color: #fff;
  box-shadow: 0 2px 6px rgba(233,112,36,0.3);

  &:hover:not(:disabled) {
    background: #d4621e;
    box-shadow: 0 4px 12px rgba(233,112,36,0.35);
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const WizardGhostBtn = styled(WizardBtn)`
  background: transparent;
  color: #555;
  border: 1px solid #e0e0e0;

  &:hover { background: #f5f5f5; color: #333; }
`;

const CLASSIFY_POLL_MS = 1500;
const CLASSIFY_PER_DOC_MS = 5000;
const CLASSIFY_GLOBAL_DEFAULT_MS = 15000;
const SUMMARY_THRESHOLD = 50;

function buildClassifierPollUrl(jobId) {
  return `${API_BASE_CLASSIFIER}/classifier/results?jobId=${encodeURIComponent(jobId)}`;
}

function parseClassifierPollPayload(raw) {
  let data = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return { files: [], ready: false, totalFiles: 0, classifiedSoFar: 0 };
    }
  }
  if (data?.body && typeof data.body === 'string') {
    try {
      data = JSON.parse(data.body);
    } catch {
      return { files: [], ready: false, totalFiles: 0, classifiedSoFar: 0 };
    }
  }
  const files = Array.isArray(data?.files) ? data.files : [];
  const totalFiles = Number(data?.totalFiles) || 0;
  const classifiedSoFar = Number(data?.classifiedSoFar) || files.length;
  return {
    files,
    ready: Boolean(data?.ready),
    totalFiles: Math.max(totalFiles, files.length),
    classifiedSoFar: Math.max(classifiedSoFar, files.length),
    uploadFailed: Boolean(data?.uploadFailed),
    uploadErrorCode: data?.uploadErrorCode || null,
    uploadError: data?.uploadError || null,
    uploadStatus: data?.uploadStatus || null,
    emptyFolder: Boolean(data?.emptyFolder),
  };
}

function isPendingSlotRow(row) {
  return Boolean(row?.isPendingSlot);
}

function isFailedRow(row) {
  return row?.status === 'FAILED';
}

/** Auto-detection finished (worker status PENDING_REVIEW). Not official until Begin Indexing. */
function isDetectionReady(row) {
  if (!row?.fileKey || isPendingSlotRow(row) || isFailedRow(row)) return false;
  if (row.status === 'PENDING_REVIEW' || row.status === 'CONFIRMED') return true;
  // Legacy rows missing status but with a real detection payload
  return (row.confidence ?? 0) > 0 && Boolean(row.category) && row.category !== 'Unknown';
}

function showRowTypeControls(row) {
  if (isFailedRow(row)) return false;
  if (isPendingSlotRow(row)) return false;
  return isDetectionReady(row);
}

function makePendingPlaceholderRow(slotIndex) {
  return {
    fileKey: `__pending_slot_${slotIndex}__`,
    fileName: `Document ${slotIndex + 1} (classifying…)`,
    category: 'Unknown',
    docClass: '',
    structuralMode: '',
    discipline: '',
    confidence: 0,
    complianceType: '',
    isPendingSlot: true,
    usedDefault: false,
  };
}

function realRows(rows) {
  return (rows || []).filter((r) => !isPendingSlotRow(r));
}

function perDocDeadline(firstSeenMs) {
  return firstSeenMs + CLASSIFY_PER_DOC_MS;
}

function shouldUseDefaultForFile(fileKey, category, firstSeenMs) {
  if (!firstSeenMs) return false;
  const timedOut = Date.now() - firstSeenMs >= CLASSIFY_PER_DOC_MS;
  return timedOut && (category === 'Unknown' || !category);
}

function applyDefaultToRow(row) {
  return mapApiFileToRow({
    fileKey: row.fileKey,
    fileName: row.fileName,
    category: row.category,
    docClass: row.docClass,
    structuralMode: row.structuralMode,
    discipline: row.discipline,
    complianceType: row.complianceType,
    confidenceScore: row.confidence,
  }, true);
}

function syncPendingSlots(realRowsList, totalFiles, pendingSlotFirstSeenRef) {
  if (!totalFiles) return realRowsList;
  const missing = Math.max(0, totalFiles - realRowsList.length);
  if (missing === 0) return realRowsList;

  const now = Date.now();
  const pendingRows = [];
  for (let k = 0; k < missing; k += 1) {
    const slotIndex = realRowsList.length + k;
    const slotKey = `slot_${slotIndex}`;
    if (!pendingSlotFirstSeenRef.current[slotKey]) {
      pendingSlotFirstSeenRef.current[slotKey] = now;
    }
    pendingRows.push(makePendingPlaceholderRow(slotIndex));
  }
  return [...realRowsList, ...pendingRows];
}

function buildRowsFromApi(apiFiles, firstSeenRef, pendingSlotFirstSeenRef, totalFiles) {
  const fileRows = (apiFiles || []).filter((f) => f.fileKey && f.fileKey !== '__meta__');
  for (const f of fileRows) {
    if (!firstSeenRef.current[f.fileKey]) {
      firstSeenRef.current[f.fileKey] = Date.now();
    }
  }

  const mapped = fileRows.map((f) => {
    const useDefault = shouldUseDefaultForFile(
      f.fileKey,
      f.category,
      firstSeenRef.current[f.fileKey],
    );
    return mapApiFileToRow(f, useDefault);
  });

  return syncPendingSlots(mapped, totalFiles, pendingSlotFirstSeenRef);
}

function getFileExt(fileName) {
  return (fileName.split('.').pop() || '').toUpperCase().slice(0, 4);
}

function defaultDocClassForFile(fileName, category, apiDocClass) {
  if (apiDocClass) return apiDocClass;
  if (category !== 'Deliverable') return '';
  const lower = String(fileName || '').toLowerCase();
  const ext = lower.includes('.') ? lower.slice(lower.lastIndexOf('.')) : '';
  if (['.dwg', '.dxf', '.svg'].includes(ext)) return 'drawing';
  const inferred = inferDocClassFromFileName(fileName);
  if (inferred) return inferred;
  if (ext === '.pdf') return 'drawing';
  return inferDocClassFromFileName(fileName) || 'drawing';
}

function mapApiFileToRow(f, useDefault = false) {
  const fileName = f.fileName || f.fileKey?.split('/').pop() || 'Document';
  const failed = f.status === 'FAILED';

  if (failed) {
    return {
      fileKey: f.fileKey,
      fileName,
      docClass: '',
      structuralMode: '',
      discipline: '',
      category: 'Unknown',
      confidence: 0,
      complianceType: '',
      status: 'FAILED',
      errorMessage: f.errorMessage || 'This file could not be processed.',
      usedDefault: false,
    };
  }

  let category = f.category || 'Unknown';
  const confidence = f.confidenceScore ?? 0;
  const unidentified = category === 'Unknown';

  if (useDefault && unidentified) {
    category = 'Deliverable';
  }

  let docClass = f.docClass || '';
  if (category === 'Deliverable' && !docClass) {
    docClass = defaultDocClassForFile(fileName, category, useDefault ? '' : f.docClass);
  }

  return {
    fileKey: f.fileKey,
    fileName,
    docClass,
    structuralMode: f.structuralMode || (docClass === 'drawing' ? 'drawing_annotation' : ''),
    discipline: f.discipline || '',
    category: category === 'Unknown' ? 'Deliverable' : category,
    confidence,
    complianceType: category === 'Requirement'
      ? (f.complianceType || (f.docClass === 'contract' ? 'CLIENT_REQUIREMENTS' : ''))
      : '',
    status: f.status || '',
    errorMessage: f.errorMessage || '',
    usedDefault: useDefault && unidentified,
    projectBuildingDataIds: f.projectBuildingDataIds || f.suggestedBuildingDataIds || [],
  };
}

function mergeFileRows(prev, incoming) {
  const byKey = Object.fromEntries(
    realRows(prev).map((r) => [r.fileKey, r]),
  );
  for (const row of incoming) {
    if (!row.fileKey || row.fileKey === '__meta__' || isPendingSlotRow(row)) continue;
    const existing = byKey[row.fileKey];
    if (existing?.userEdited && row.status !== 'FAILED') {
      byKey[row.fileKey] = {
        ...row,
        category: existing.category,
        docClass: existing.docClass,
        structuralMode: existing.structuralMode,
        complianceType: existing.complianceType,
        projectBuildingDataIds: existing.projectBuildingDataIds ?? row.projectBuildingDataIds,
        userEdited: true,
        usedDefault: false,
      };
    } else if (isFailedRow(existing) && row.status !== 'FAILED') {
      // A confirmed FAILED row is sticky: classifierGetResults reads via a DynamoDB GSI
      // (eventually consistent), so a later poll can transiently omit or misreport this
      // file's status. Never let that silently revert an already-confirmed failure.
      byKey[row.fileKey] = existing;
    } else {
      byKey[row.fileKey] = row;
    }
  }
  return Object.values(byKey);
}

function groupFiles(files) {
  const classified = realRows(files).filter(f => !isFailedRow(f));
  const requirements = classified.filter(f => f.category === 'Requirement');
  const deliverables = classified.filter(f => f.category === 'Deliverable');
  const unknown      = classified.filter(f => f.category === 'Unknown');
  return { requirements, deliverables, unknown };
}

function rowGroupKey(row) {
  const category = row.category === 'Unknown' ? 'Deliverable' : row.category;
  return `${category}|${row.docClass || ''}|${row.complianceType || ''}`;
}

function buildRowGroups(files) {
  const groups = {};
  for (const row of realRows(files)) {
    const key = rowGroupKey(row);
    if (!groups[key]) {
      groups[key] = {
        key,
        category: row.category === 'Unknown' ? 'Deliverable' : row.category,
        docClass: row.docClass || '',
        complianceType: row.complianceType || '',
        fileKeys: [],
        count: 0,
      };
    }
    groups[key].fileKeys.push(row.fileKey);
    groups[key].count += 1;
  }
  return Object.values(groups).sort((a, b) => b.count - a.count);
}

function summaryGroupTitle(group) {
  if (group.category === 'Requirement') {
    const complianceLabel = COMPLIANCE_TYPES.find((t) => t.value === group.complianceType)?.label;
    return complianceLabel || 'Requirement';
  }
  return DOC_CLASS_LABELS[group.docClass] || group.docClass || 'Drawing';
}

function AppliesToMultiSelect({ selectedIds, entityOptions, onChange, disabled }) {
  const ids = selectedIds || [];
  const toggle = (entityId) => {
    const next = ids.includes(entityId)
      ? ids.filter((x) => x !== entityId)
      : [...ids, entityId];
    onChange(next);
  };
  return (
    <AppliesToBox title={formatAppliesToLabel(ids, entityOptions)}>
      <AppliesToSummary>
        <span>{ids.length ? formatAppliesToLabel(ids, entityOptions) : 'All buildings'}</span>
        {ids.length > 0 && (
          <AppliesToClear type="button" onClick={() => onChange([])} disabled={disabled} title="Site-wide">×</AppliesToClear>
        )}
      </AppliesToSummary>
      <AppliesToChecks>
        {entityOptions.map((ent) => (
          <label key={ent.id}>
            <input
              type="checkbox"
              checked={ids.includes(ent.id)}
              onChange={() => toggle(ent.id)}
              disabled={disabled}
            />
            {ent.name || ent.id}
          </label>
        ))}
      </AppliesToChecks>
    </AppliesToBox>
  );
}

function FileTypeControls({
  row,
  showTypeControls,
  onCategoryChange,
  onComplianceChange,
  onDocClassChange,
  onAppliesToChange,
  showAppliesTo,
  entityOptions,
  disabled = false,
}) {
  if (!showTypeControls) {
    return <ClassifyingBadge>Detecting…</ClassifyingBadge>;
  }

  const category = row.category === 'Unknown' ? 'Deliverable' : row.category;

  return (
    <>
      <CategorySelect
        value={category}
        onChange={(e) => onCategoryChange(e.target.value)}
        disabled={disabled}
      >
        <option value="Requirement">Requirement</option>
        <option value="Deliverable">Deliverable</option>
      </CategorySelect>
      {category === 'Requirement' ? (
        <CategorySelect
          value={row.complianceType || ''}
          onChange={(e) => onComplianceChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">— Select type —</option>
          {COMPLIANCE_TYPES.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </CategorySelect>
      ) : (
        <CategorySelect
          value={row.docClass || inferDocClassFromFileName(row.fileName) || 'drawing'}
          onChange={(e) => onDocClassChange(e.target.value)}
          disabled={disabled}
        >
          {DELIVERABLE_DOC_CLASS_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {DOC_CLASS_LABELS[value] || value}
            </option>
          ))}
        </CategorySelect>
      )}
      {showAppliesTo && onAppliesToChange && (
        <AppliesToMultiSelect
          selectedIds={row.projectBuildingDataIds}
          entityOptions={entityOptions}
          onChange={onAppliesToChange}
          disabled={disabled}
        />
      )}
    </>
  );
}


export default function ClassifierModal({
  isOpen, onClose, jobId, folderName, totalFiles, onConfirmed,
  project, onConstructionTypeConfirmed, onZoningDesignationConfirmed,
  projectId, requirementId, linkId,
}) {
  const auth = useAuth();
  const { entities: projectEntities } = React.useMemo(
    () => loadProjectEntities(project || {}),
    [project]
  );
  const showAppliesTo = projectEntities.length > 1;
  const [rows, setRows]         = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pollTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const classifyStartedRef = useRef(0);
  const fileFirstSeenRef = useRef({});
  const pendingSlotFirstSeenRef = useRef({});
  const pollStoppedRef = useRef(false);
  const [classifyProgress, setClassifyProgress] = useState({ done: 0, total: 0 });
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [usingDefaults, setUsingDefaults] = useState(false);
  const [awaitingFileList, setAwaitingFileList] = useState(false);
  const [uploadFailure, setUploadFailure] = useState(null);
  const [emptyFolder, setEmptyFolder] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardConstructionType, setWizardConstructionType] = useState('');
  const [zoningWizardOpen, setZoningWizardOpen] = useState(false);
  const [zoningInput, setZoningInput] = useState('');
  const [viewMode, setViewMode] = useState('detail');
  const [expandedGroups, setExpandedGroups] = useState({});
  const pendingConfirmRef = useRef(false);
  const firedVerificationOpenedRef = useRef(false);
  const firedUploadFailureRef = useRef(false);
  const capturedFailedFileKeysRef = useRef(new Set());
  const lastPollPayloadRef = useRef({ files: [], ready: false, totalFiles: 0, classifiedSoFar: 0 });
  const CONSTRUCTION_LIST = project?.occupancyRegion === 'CA'
    ? CONSTRUCTION_TYPES_CA
    : CONSTRUCTION_TYPES_US;
  const OCCUPANCY_LIST = project?.occupancyRegion === 'CA'
    ? OCCUPANCY_TYPES_CA
    : OCCUPANCY_TYPES_US;

  const PROJECT_TYPE_LABELS = {
    new_construction: 'New Construction',
    addition: 'Addition',
    alteration: 'Alteration',
    change_of_use: 'Change of Use',
  };

  useEffect(() => {
    if (!isOpen || !jobId) return;
    setRows([]);
    setIsLoading(true);
    setUsingDefaults(false);
    setAwaitingFileList(false);
    setUploadFailure(null);
    setEmptyFolder(false);
    setClassifyProgress({ done: 0, total: 0 });
    setTotalFileCount(0);
    setWizardOpen(false);
    setWizardConstructionType('');
    setViewMode('detail');
    setExpandedGroups({});
    pollStoppedRef.current = false;
    fileFirstSeenRef.current = {};
    pendingSlotFirstSeenRef.current = {};
    firedVerificationOpenedRef.current = false;
    lastPollPayloadRef.current = { files: [], ready: false, totalFiles: 0, classifiedSoFar: 0 };
    classifyStartedRef.current = Date.now();
    pollForResults(jobId);

    fallbackTimerRef.current = setTimeout(() => {
      if (pollStoppedRef.current) return;
      setUsingDefaults(true);
      setIsLoading(false);
    }, CLASSIFY_GLOBAL_DEFAULT_MS);

    return () => {
      pollStoppedRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, [isOpen, jobId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const tick = setInterval(() => {
      const totalExpected =
        totalFileCount ||
        lastPollPayloadRef.current.totalFiles ||
        classifyProgress.total ||
        totalFiles ||
        0;
      setRows((prev) => {
        const pollPayload = lastPollPayloadRef.current;
        const pollApiRows = buildRowsFromApi(
          pollPayload.files,
          fileFirstSeenRef,
          pendingSlotFirstSeenRef,
          totalExpected,
        );
        const mergedReal = mergeFileRows(prev, realRows(pollApiRows)).map((row) => {
          if (row.userEdited || isFailedRow(row)) return row;
          const firstSeen = fileFirstSeenRef.current[row.fileKey];
          if (!firstSeen || row.usedDefault) return row;
          if (!shouldUseDefaultForFile(row.fileKey, row.category, firstSeen)) return row;
          return applyDefaultToRow(row);
        });
        return syncPendingSlots(mergedReal, totalExpected, pendingSlotFirstSeenRef);
      });

      const elapsed = Date.now() - classifyStartedRef.current;
      if (elapsed >= CLASSIFY_GLOBAL_DEFAULT_MS) {
        setUsingDefaults(true);
        setIsLoading(false);
      }
    }, 400);

    return () => clearInterval(tick);
  }, [isOpen, totalFileCount, classifyProgress.total, totalFiles]);

  useEffect(() => {
    if (!isOpen) return;
    const totalExpected = totalFileCount || classifyProgress.total || totalFiles || 0;
    if (totalExpected >= SUMMARY_THRESHOLD) {
      setViewMode('summary');
    }
  }, [isOpen, totalFileCount, classifyProgress.total, totalFiles]);

  useEffect(() => {
    if (!isOpen) return;
    const newlyFailed = realRows(rows).filter(
      (row) => isFailedRow(row) && !capturedFailedFileKeysRef.current.has(row.fileKey)
    );
    if (newlyFailed.length === 0) return;
    newlyFailed.forEach((row) => {
      capturedFailedFileKeysRef.current.add(row.fileKey);
      phDocumentClassificationFailed({
        projectId,
        jobId,
        scope: 'file',
        reason: 'FILE_CLASSIFICATION_FAILED',
        errorMessage: row.errorMessage,
      });
    });
  }, [isOpen, rows, projectId, jobId]);

  const applyPollResults = (pollPayload) => {
    if (pollPayload.emptyFolder) {
      pollStoppedRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
      setEmptyFolder(true);
      setAwaitingFileList(false);
      setIsLoading(false);
      return;
    }
    if (pollPayload.uploadFailed) {
      const msg = uploadPollErrorMessage(pollPayload);
      pollStoppedRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
      setUploadFailure({ message: msg, code: pollPayload.uploadErrorCode });
      setAwaitingFileList(false);
      setIsLoading(false);
      message.error(msg);
      if (!firedUploadFailureRef.current) {
        firedUploadFailureRef.current = true;
        phDocumentClassificationFailed({
          projectId,
          jobId,
          scope: 'upload',
          reason: pollPayload.uploadErrorCode,
          errorMessage: msg,
        });
      }
      return;
    }

    const { files: apiFiles, ready, totalFiles: totalFilesCount, classifiedSoFar } = pollPayload;
    lastPollPayloadRef.current = pollPayload;
    const totalExpected =
      totalFilesCount ||
      totalFileCount ||
      lastPollPayloadRef.current.totalFiles ||
      classifyProgress.total ||
      totalFiles ||
      0;
    const apiRows = buildRowsFromApi(
      apiFiles,
      fileFirstSeenRef,
      pendingSlotFirstSeenRef,
      totalExpected,
    );

    setRows((prev) => {
      const mergedReal = mergeFileRows(prev, realRows(apiRows));
      return syncPendingSlots(mergedReal, totalExpected, pendingSlotFirstSeenRef);
    });

    const nextRealCount = realRows(apiRows).length;
    if (nextRealCount > 0 || apiRows.some(isPendingSlotRow)) {
      setUsingDefaults(realRows(apiRows).some((r) => r.usedDefault));
      setAwaitingFileList(false);
      setIsLoading(false);

      if (nextRealCount > 0 && !firedVerificationOpenedRef.current) {
        firedVerificationOpenedRef.current = true;
        phVerificationModalOpened({
          projectId,
          docCount: nextRealCount,
          unknownCount: realRows(apiRows).filter((r) => r.category === 'Unknown').length,
        });
      }
    }

    if (ready && nextRealCount >= totalExpected && totalExpected > 0) {
      pollStoppedRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
      setIsLoading(false);
      return;
    }

    const elapsed = Date.now() - classifyStartedRef.current;
    if (elapsed >= CLASSIFY_PER_DOC_MS) {
      setIsLoading(false);
      if (nextRealCount === 0 && totalExpected > 0 && classifiedSoFar === 0) {
        setAwaitingFileList(true);
      }
    }
  };

  const pollForResults = (jid) => {
    let attempts = 0;
    const MAX_ATTEMPTS = 120;

    const poll = async () => {
      if (pollStoppedRef.current) return;
      attempts++;
      try {
        const idToken = localStorage.getItem('id_token');
        const res = await axios.get(
          buildClassifierPollUrl(jid),
          { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
        );

        const pollPayload = parseClassifierPollPayload(res.data);

        setClassifyProgress({
          done: pollPayload.classifiedSoFar,
          total: pollPayload.totalFiles,
        });
        if (pollPayload.totalFiles > 0) {
          setTotalFileCount(pollPayload.totalFiles);
        }

        applyPollResults(pollPayload);

        if (pollPayload.uploadFailed) return;

        if (
          pollPayload.classifiedSoFar >= pollPayload.totalFiles &&
          pollPayload.totalFiles > 0 &&
          realRows(buildRowsFromApi(
            pollPayload.files,
            fileFirstSeenRef,
            pendingSlotFirstSeenRef,
            pollPayload.totalFiles,
          )).length < pollPayload.totalFiles
        ) {
          console.warn('[ClassifierModal] API reports detected files but UI rows missing — retrying poll');
          pollTimerRef.current = setTimeout(poll, 250);
          return;
        }
        if (pollStoppedRef.current) return;
      } catch (err) {
        console.error('[ClassifierModal] Poll error:', err);
        if (Date.now() - classifyStartedRef.current >= CLASSIFY_PER_DOC_MS) {
          setIsLoading(false);
          setUsingDefaults(true);
        }
      }

      if (pollStoppedRef.current) return;

      if (attempts < MAX_ATTEMPTS) {
        pollTimerRef.current = setTimeout(poll, CLASSIFY_POLL_MS);
      } else {
        pollStoppedRef.current = true;
        setIsLoading(false);
        message.error('Type detection timed out. Adjust suggestions below or close and try again.');
      }
    };

    poll();
  };

  const updateComplianceType = (fileKey, newType) => {
      setRows(prev => prev.map(r =>
          r.fileKey === fileKey ? { ...r, complianceType: newType, userEdited: true } : r
      ));
  };

  const updateDocClass = (fileKey, newDocClass) => {
    setRows(prev => prev.map(r => {
      if (r.fileKey !== fileKey) return r;
      return {
        ...r,
        docClass: newDocClass,
        structuralMode: newDocClass === 'drawing' ? 'drawing_annotation' : r.structuralMode,
        userEdited: true,
      };
    }));
  };

  const updateAppliesTo = (fileKey, entityIds) => {
    setRows(prev => prev.map(r => (
      r.fileKey === fileKey
        ? { ...r, projectBuildingDataIds: entityIds || [], userEdited: true }
        : r
    )));
  };

  const updateCategory = (fileKey, newCategory) => {
    setRows(prev => prev.map(r => {
      if (r.fileKey !== fileKey) return r;
      const inferred = inferDocClassFromFileName(r.fileName);
      return {
        ...r,
        category: newCategory,
        docClass: newCategory === 'Deliverable'
          ? (r.docClass || inferred || 'drawing')
          : r.docClass,
        structuralMode: newCategory === 'Deliverable' && (r.docClass === 'drawing' || inferred === 'drawing')
          ? 'drawing_annotation'
          : (newCategory === 'Requirement' ? '' : r.structuralMode),
        complianceType: newCategory === 'Requirement'
          ? (r.complianceType || (r.docClass === 'contract' ? 'CLIENT_REQUIREMENTS' : ''))
          : '',
        userEdited: true,
      };
    }));
  };

  const bulkUpdateGroup = (groupKey, updates) => {
    setRows((prev) => prev.map((r) => {
      if (isPendingSlotRow(r) || rowGroupKey(r) !== groupKey) return r;
      const category = updates.category ?? r.category;
      const inferred = inferDocClassFromFileName(r.fileName);
      const docClass = updates.docClass !== undefined
        ? updates.docClass
        : (category === 'Deliverable' ? (r.docClass || inferred || 'drawing') : r.docClass);
      return {
        ...r,
        category,
        docClass,
        structuralMode: category === 'Deliverable' && docClass === 'drawing'
          ? 'drawing_annotation'
          : (category === 'Requirement' ? '' : r.structuralMode),
        complianceType: category === 'Requirement'
          ? (updates.complianceType ?? r.complianceType ?? (docClass === 'contract' ? 'CLIENT_REQUIREMENTS' : ''))
          : '',
        userEdited: true,
      };
    }));
  };
  const hasBuildingCodeRequirement = rows.some(
    r => r.category === 'Requirement' && r.complianceType === 'BUILDING_CODE'
  );
  const hasZoningRequirement = rows.some(
    r => r.category === 'Requirement' && r.complianceType === 'ZONING'
  );

  // Construction-type gate (building codes), runs after the zoning gate.
  const continueToConstructionGate = async () => {
    if (hasBuildingCodeRequirement && !project?.constructionType) {
      setWizardConstructionType('');
      setWizardOpen(true);
      return;
    }
    await doConfirm();
  };

  const handleConfirm = async () => {
    // Zoning gate first: prompt for the zoning designation if a zoning bylaw is being
    // indexed but the project has no designation set. The entered value flows into
    // projectContext via the zoningInput fallback in doConfirm.
    if (hasZoningRequirement && !project?.zoningDesignation) {
      setZoningWizardOpen(true);
      return;
    }
    await continueToConstructionGate();
  };

  // Called by both the zoning prompt's "Skip" and "Confirm" buttons.
  const proceedAfterZoning = async () => {
    setZoningWizardOpen(false);
    if (zoningInput.trim()) {
      onZoningDesignationConfirmed && onZoningDesignationConfirmed(zoningInput.trim());
    }
    await continueToConstructionGate();
  };

  const doConfirm = async () => {
    // User dropdown selections become official only when this POST succeeds (classifierConfirm → CONFIRMED).
    const allReal = realRows(rows);
    const confirmableRows = allReal.filter((r) => !isFailedRow(r));
    const skippedCount = allReal.length - confirmableRows.length;
    const totalExpected = totalFileCount || totalFiles || classifyProgress.total;

    // Wait only on the good files — failed ones will never "detect".
    if (totalExpected > 0 && allReal.length < totalExpected) {
      message.warning(`${totalExpected - allReal.length} file(s) still detecting — wait for suggestions before indexing.`);
      return;
    }

    if (confirmableRows.length === 0) {
      message.error('No supported files to index.');
      return;
    }

    const missingDeliverableType = confirmableRows.some(
      r => r.category === 'Deliverable' && !r.docClass
    );
    if (missingDeliverableType) {
      message.error('Select a deliverable type (e.g. Drawing) for each deliverable file.');
      return;
    }

    setIsSubmitting(true);
    try {
      const idToken = localStorage.getItem('id_token');
      const confirmations = confirmableRows.map(r => ({
        fileKey:        r.fileKey,
        category:       r.category,
        docClass:       r.docClass,
        discipline:     r.discipline,
        structuralMode: r.structuralMode || (r.docClass === 'drawing' ? 'drawing_annotation' : ''),
        complianceType: r.complianceType,
        projectBuildingDataIds: r.projectBuildingDataIds || [],
      }));
      const projectContext = project ? {
        jurisdiction:           project.occupancyRegion,
        majorOccupancyCode:     project.majorOccupancy,
        zoningDesignation:      project.zoningDesignation || zoningInput.trim(),
        allOccupancyCodes:      project.occupancyCodes || [],
        buildingClassification: project.buildingClassification,
        projectType:            project.type,
        storeysAboveGrade:      project.storeysAboveGrade,
        storeysBelowGrade:      project.storeysBelowGrade || 0,
        buildingFootprintArea:  project.buildingFootprintArea,
        adjacentStreets:        project.adjacentStreets,
        constructionType:       project.constructionType || wizardConstructionType || null,
        highBuilding:           project.highBuilding || false,
        sprinklerSystem:        project.sprinklerSystem,
        hazardousSubstances:    project.hazardousSubstances || false,
      } : {};
      const cleanContext = Object.fromEntries(
        Object.entries(projectContext).filter(([_, v]) => v != null && v !== '')
      );
      await axios.post(
        `${API_BASE_CLASSIFIER}/classifier/confirm`,
        { jobId, confirmations, projectContext: cleanContext, projectId, requirementId, linkId, companyId: project?.companyId || '' },
        { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
      );
      phExtractionApproved({
        projectId,
        requirementDocCount: confirmableRows.filter((r) => r.category === 'Requirement').length,
      });
      try {
        // Read back in ExtractedRequirementsPage.jsx when register_populated
        // fires, to compute extraction duration_ms. sessionStorage (not a
        // ref) since extraction happens server-side and the register may be
        // viewed after this modal/component has unmounted.
        sessionStorage.setItem(`ph_extraction_started_${projectId}`, String(Date.now()));
      } catch {
        /* sessionStorage unavailable — duration just won't be reported */
      }
      onConfirmed && onConfirmed(confirmableRows, jobId);
      onClose();
    } catch (err) {
      console.error('[ClassifierModal] Confirm error:', err);
      message.error('Failed to confirm. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWizardConfirm = async () => {
    if (!wizardConstructionType) {
      message.warning('Please select a construction type or skip.');
      return;
    }
    onConstructionTypeConfirmed && onConstructionTypeConfirmed(wizardConstructionType);
    setWizardOpen(false);
    await doConfirm();
  };


  if (!isOpen) return null;

  const { requirements, deliverables, unknown } = groupFiles(rows);
  const reqCount = requirements.length;
  const skipCount = deliverables.length + unknown.length;
  const confirmableRows = realRows(rows);
  const failedRows = confirmableRows.filter(isFailedRow);
  const goodRows = confirmableRows.filter((r) => !isFailedRow(r));
  const totalExpected =
    totalFileCount ||
    lastPollPayloadRef.current.totalFiles ||
    totalFiles ||
    classifyProgress.total ||
    0;
  const detectedCount = confirmableRows.filter(isDetectionReady).length;
  const detectedDone = Math.max(detectedCount, classifyProgress.done || 0);
  const failedCount = failedRows.length;
  const awaitingScanCount = Math.max(0, totalExpected - detectedDone - failedCount);
  const pendingSlots = rows.filter(isPendingSlotRow);
  const summaryGroups = buildRowGroups(rows);
  const rowsByKey = Object.fromEntries(realRows(rows).map((r) => [r.fileKey, r]));
  const allDetectionsReady =
    goodRows.length > 0 && goodRows.every(isDetectionReady);
  const reviewReady =
    !isLoading &&
    pendingSlots.length === 0 &&
    (totalExpected === 0 || confirmableRows.length >= totalExpected) &&
    allDetectionsReady;
  const canConfirmIndexing =
    reviewReady &&
    goodRows.length > 0 &&
    !goodRows.some(r => r.category === 'Requirement' && !r.complianceType) &&
    !goodRows.some(r => r.category === 'Deliverable' && !r.docClass);

  return (
    <Overlay onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal
        onClick={e => e.stopPropagation()}
        data-tour="classify-modal"
        style={viewMode === 'summary' ? { maxWidth: '780px' } : undefined}
      >

        <ModalHeader>
          <ModalTitleGroup>
            <ModalTitle>Review Detected Document Types</ModalTitle>
            <ModalSubtitle>
              {uploadFailure
                ? uploadFailure.message
                : failedRows.length > 0
                ? `${failedRows.length} file${failedRows.length !== 1 ? 's' : ''} could not be processed — rename or fix in Drive, then re-link the folder.`
                : isLoading && rows.length === 0
                ? `Detecting document types…${classifyProgress.total > 0 ? ` (${classifyProgress.done} of ${classifyProgress.total} done)` : ''}`
                : awaitingScanCount > 0
                ? `${awaitingScanCount} file${awaitingScanCount !== 1 ? 's' : ''} still detecting — ${detectedDone} suggestion${detectedDone !== 1 ? 's' : ''} ready.`
                : usingDefaults
                ? 'Some files used manual type defaults — review before clicking Begin Indexing.'
                : viewMode === 'summary'
                ? `${confirmableRows.length} suggested types grouped below. Adjust if needed, then click Begin Indexing to confirm.`
                : `CODiii detected types for ${confirmableRows.length} document${confirmableRows.length !== 1 ? 's' : ''}. Adjust if needed — nothing is final until you click Begin Indexing.`}
            </ModalSubtitle>
          </ModalTitleGroup>
          <CloseBtn onClick={onClose} aria-label="Close">
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </CloseBtn>
        </ModalHeader>

        <ModalBody>
          <ScanInfo>
            <ScanInfoItem><ScanLabel>Folder:</ScanLabel><ScanValue>{folderName || 'Linked folder'}</ScanValue></ScanInfoItem>
            <ScanInfoItem><ScanLabel>Total:</ScanLabel><ScanValue>{totalFileCount || totalFiles || '—'} documents</ScanValue></ScanInfoItem>
          </ScanInfo>

          {emptyFolder ? (
            <ErrorBanner>
              This folder has no files, so there's nothing to upload. Add documents to the folder in Drive or SharePoint, then re-link it.
            </ErrorBanner>
          ) : uploadFailure ? (
            <ErrorBanner>
              {uploadFailure.message}
            </ErrorBanner>
          ) : isLoading && rows.length === 0 ? (
            <LoadingWrap>
              <Spinner />
              <span>
                Detecting document types…
                {classifyProgress.total > 0
                  ? ` (${classifyProgress.done} of ${classifyProgress.total} done)`
                  : ' checking first pages for signals…'}
              </span>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                Sampling first pages for type detection — full indexing runs after you click Begin Indexing.
              </span>
            </LoadingWrap>
          ) : awaitingFileList && rows.length === 0 ? (
            <LoadingWrap>
              <Spinner />
              <span>
                Preparing file list
                {totalFileCount > 0 ? ` (${totalFileCount} document${totalFileCount === 1 ? '' : 's'})` : ''}…
              </span>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                Defaults will apply as soon as files are ready.
              </span>
            </LoadingWrap>
          ) : (
            <>
              {failedRows.length > 0 && (
                <ErrorBanner>
                  {failedRows.map((f) => (
                    <div key={f.fileKey}>
                      <strong>{f.fileName}</strong>
                      {f.errorMessage ? ` — ${f.errorMessage}` : ''}
                    </div>
                  ))}
                </ErrorBanner>
              )}
              {isLoading && rows.length > 0 && (
                <LoadingWrap style={{ marginBottom: '0.75rem', minHeight: 'auto', padding: '0.75rem 1rem' }}>
                  <Spinner />
                  <span style={{ fontSize: '0.85rem' }}>
                    Still detecting… ({detectedDone}{totalExpected > 0 ? ` of ${totalExpected}` : ''} ready)
                  </span>
                </LoadingWrap>
              )}

              {confirmableRows.length > 0 && totalExpected >= SUMMARY_THRESHOLD && (
                <ViewToggleBar>
                  <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                    {summaryGroups.length} group{summaryGroups.length !== 1 ? 's' : ''} detected
                  </span>
                  <ViewToggle>
                    <ViewToggleBtn
                      type="button"
                      $active={viewMode === 'summary'}
                      onClick={() => setViewMode('summary')}
                    >
                      Summary
                    </ViewToggleBtn>
                    <ViewToggleBtn
                      type="button"
                      $active={viewMode === 'detail'}
                      onClick={() => setViewMode('detail')}
                    >
                      All files
                    </ViewToggleBtn>
                  </ViewToggle>
                </ViewToggleBar>
              )}

              {viewMode === 'summary' && summaryGroups.length > 0 && (
                <>
                  {summaryGroups.map((group) => (
                    <SummaryGroupCard key={group.key}>
                      <SummaryGroupHeader>
                        <SummaryGroupCount>{group.count}</SummaryGroupCount>
                        <SummaryGroupMeta>
                          <SummaryGroupTitle>
                            {group.category} · {summaryGroupTitle(group)}
                          </SummaryGroupTitle>
                        </SummaryGroupMeta>
                        <SummaryGroupActions>
                          {reviewReady ? (
                            <>
                              <CategorySelect
                                value={group.category}
                                onChange={(e) => bulkUpdateGroup(group.key, { category: e.target.value })}
                              >
                                <option value="Requirement">Requirement</option>
                                <option value="Deliverable">Deliverable</option>
                              </CategorySelect>
                              {group.category === 'Requirement' ? (
                                <CategorySelect
                                  value={group.complianceType || ''}
                                  onChange={(e) => bulkUpdateGroup(group.key, { complianceType: e.target.value })}
                                >
                                  <option value="">— Select type —</option>
                                  {COMPLIANCE_TYPES.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                  ))}
                                </CategorySelect>
                              ) : (
                                <CategorySelect
                                  value={group.docClass || 'drawing'}
                                  onChange={(e) => bulkUpdateGroup(group.key, { docClass: e.target.value })}
                                >
                                  {DELIVERABLE_DOC_CLASS_OPTIONS.map((value) => (
                                    <option key={value} value={value}>
                                      {DOC_CLASS_LABELS[value] || value}
                                    </option>
                                  ))}
                                </CategorySelect>
                              )}
                            </>
                          ) : (
                            <ClassifyingBadge>Detecting…</ClassifyingBadge>
                          )}
                        </SummaryGroupActions>
                      </SummaryGroupHeader>
                      <ExpandLink
                        type="button"
                        onClick={() => setExpandedGroups((prev) => ({
                          ...prev,
                          [group.key]: !prev[group.key],
                        }))}
                      >
                        {expandedGroups[group.key] ? 'Hide files' : `Show ${group.count} file${group.count !== 1 ? 's' : ''}`}
                      </ExpandLink>
                      {expandedGroups[group.key] && (
                        <ExpandedFileList>
                          {group.fileKeys.map((fileKey) => (
                            <div key={fileKey}>{rowsByKey[fileKey]?.fileName || fileKey}</div>
                          ))}
                        </ExpandedFileList>
                      )}
                    </SummaryGroupCard>
                  ))}
                </>
              )}

              {viewMode === 'detail' && (
              <>
              {pendingSlots.length > 0 && (
                <DocGroup>
                  <DocGroupHeader>
                    <DocGroupTitle $type="Unknown">Scanning</DocGroupTitle>
                    <DocGroupNote>— waiting for type detection (suggestions appear when ready)</DocGroupNote>
                    <DocGroupCount $type="Unknown">{pendingSlots.length}</DocGroupCount>
                  </DocGroupHeader>
                  <Divider />
                  {pendingSlots.map(f => (
                    <DocRow key={f.fileKey}>
                      <DocIcon>…</DocIcon>
                      <DocInfo>
                        <DocName title={f.fileName}>{f.fileName}</DocName>
                      </DocInfo>
                      <ClassifyingBadge>Detecting…</ClassifyingBadge>
                    </DocRow>
                  ))}
                </DocGroup>
              )}

              {/* Requirements group */}
              {requirements.length > 0 && (
                <DocGroup>
                  <DocGroupHeader>
                    <DocGroupTitle $type="Requirement">Requirement</DocGroupTitle>
                    <DocGroupNote>— will be available for extraction later</DocGroupNote>
                    <DocGroupCount $type="Requirement">{requirements.length}</DocGroupCount>
                  </DocGroupHeader>
                  <Divider />
                  {requirements.map(f => (
                    <DocRow key={f.fileKey}>
                      <DocIcon>{getFileExt(f.fileName)}</DocIcon>
                      <DocInfo>
                          <DocName title={f.fileName}>{f.fileName}</DocName>
                      </DocInfo>
                      <FileTypeControls
                        row={f}
                        showTypeControls={showRowTypeControls(f)}
                        onCategoryChange={(value) => {
                          phDocumentReclassified({ projectId, fromType: f.category, toType: value });
                          updateCategory(f.fileKey, value);
                        }}
                        onComplianceChange={(value) => updateComplianceType(f.fileKey, value)}
                        onDocClassChange={(value) => updateDocClass(f.fileKey, value)}
                        showAppliesTo={showAppliesTo}
                        entityOptions={projectEntities}
                        onAppliesToChange={(ids) => updateAppliesTo(f.fileKey, ids)}
                      />
                  </DocRow>
                  ))}
                </DocGroup>
              )}

              {/* Deliverables group */}
              {deliverables.length > 0 && (
                <DocGroup>
                  <DocGroupHeader>
                    <DocGroupTitle $type="Deliverable">Deliverable</DocGroupTitle>
                    <DocGroupNote>— drawings route to Review labeling; other deliverables indexed for search</DocGroupNote>
                    <DocGroupCount $type="Deliverable">{deliverables.length}</DocGroupCount>
                  </DocGroupHeader>
                  <Divider />
                  {deliverables.map(f => (
                    <DocRow key={f.fileKey}>
                      <DocIcon>{getFileExt(f.fileName)}</DocIcon>
                      <DocInfo>
                        <DocName title={f.fileName}>{f.fileName}</DocName>
                      </DocInfo>
                      <FileTypeControls
                        row={f}
                        showTypeControls={showRowTypeControls(f)}
                        onCategoryChange={(value) => {
                          phDocumentReclassified({ projectId, fromType: f.category, toType: value });
                          updateCategory(f.fileKey, value);
                        }}
                        onComplianceChange={(value) => updateComplianceType(f.fileKey, value)}
                        onDocClassChange={(value) => updateDocClass(f.fileKey, value)}
                        showAppliesTo={showAppliesTo}
                        entityOptions={projectEntities}
                        onAppliesToChange={(ids) => updateAppliesTo(f.fileKey, ids)}
                      />
                    </DocRow>
                  ))}
                </DocGroup>
              )}

              {/* Unknown group */}
              {unknown.length > 0 && (
                <DocGroup>
                  <DocGroupHeader>
                    <DocGroupTitle $type="Unknown">Unknown</DocGroupTitle>
                    <DocGroupNote>— low confidence, defaulting to Deliverable. Reassign if needed.</DocGroupNote>
                    <DocGroupCount $type="Unknown">{unknown.length}</DocGroupCount>
                  </DocGroupHeader>
                  <Divider />
                  {unknown.map(f => (
                    <DocRow key={f.fileKey} $unknown>
                      <DocIcon>{getFileExt(f.fileName)}</DocIcon>
                      <DocInfo>
                        <DocName title={f.fileName}>{f.fileName}</DocName>
                      </DocInfo>
                      <FileTypeControls
                        row={f}
                        showTypeControls={showRowTypeControls(f)}
                        onCategoryChange={(value) => {
                          phDocumentReclassified({ projectId, fromType: f.category, toType: value });
                          updateCategory(f.fileKey, value);
                        }}
                        onComplianceChange={(value) => updateComplianceType(f.fileKey, value)}
                        onDocClassChange={(value) => updateDocClass(f.fileKey, value)}
                        showAppliesTo={showAppliesTo}
                        entityOptions={projectEntities}
                        onAppliesToChange={(ids) => updateAppliesTo(f.fileKey, ids)}
                      />
                    </DocRow>
                  ))}
                </DocGroup>
              )}

              </>
              )}

              {reqCount > 0 && (
                <ProcessingEstimate>
                  <svg width="18" height="18" fill="none" stroke="#2563eb" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span>
                    <strong>{reqCount} requirement document{reqCount !== 1 ? 's' : ''}</strong> will be available for extraction once indexing is complete.
                    All {rows.length} documents will be indexed and searchable immediately.
                  </span>
                </ProcessingEstimate>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <FooterLeft>
            {rows.length > 0 && (
              awaitingScanCount > 0
                ? `${awaitingScanCount} detecting · ${reqCount} requirement${reqCount !== 1 ? 's' : ''} · ${skipCount} deliverable${skipCount !== 1 ? 's' : ''}`
                : `${reqCount} requirement${reqCount !== 1 ? 's' : ''} · ${skipCount} deliverable${skipCount !== 1 ? 's' : ''}`
            )}
          </FooterLeft>
          <FooterRight>
            <SecondaryBtn onClick={onClose} disabled={isSubmitting}>
              {emptyFolder ? 'Close' : 'Cancel'}
            </SecondaryBtn>
            <PrimaryBtn
              onClick={handleConfirm}
              disabled={
                isSubmitting || rows.length === 0 || awaitingFileList || !canConfirmIndexing
              }
            >
              {isSubmitting
                ? 'Starting…'
                : awaitingScanCount > 0
                ? `Detecting (${detectedDone}/${totalExpected})…`
                : failedRows.length > 0
                ? `Begin Indexing (${goodRows.length})`
                : 'Begin Indexing'}
            </PrimaryBtn>
          </FooterRight>
        </ModalFooter>

      </Modal>
      {wizardOpen && (
        <WizardOverlay onClick={e => e.stopPropagation()}>
          <WizardModal>
            <WizardHeader>
              <WizardHeaderLeft>
                <WizardIconRing>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </WizardIconRing>
                <div>
                  <WizardTitle>Construction Type Required</WizardTitle>
                  <WizardSubtitle>One more parameter before indexing begins</WizardSubtitle>
                </div>
              </WizardHeaderLeft>
              <CloseBtn onClick={() => setWizardOpen(false)} aria-label="Back to classifier">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </CloseBtn>
            </WizardHeader>

            <WizardBody>
              <div>
                <WizardSectionLabel>Your Project Parameters</WizardSectionLabel>
                <ParamGrid>
                  <ParamRow>
                    <ParamLabel>Jurisdiction</ParamLabel>
                    <ParamValue>
                      {project?.occupancyRegion === 'CA' ? 'Canada (NBC/OBC)' : 'United States (IBC)'}
                    </ParamValue>
                  </ParamRow>
                  <ParamRow>
                    <ParamLabel>Major Occupancy</ParamLabel>
                    <ParamValue>
                      {project?.majorOccupancy
                        ? (OCCUPANCY_LIST.find(([key]) => key === project.majorOccupancy)?.[1] || project.majorOccupancy)
                        : <MissingValue>Not set</MissingValue>}
                    </ParamValue>
                  </ParamRow>
                  <ParamRow>
                    <ParamLabel>Building Classification</ParamLabel>
                    <ParamValue>
                      {project?.buildingClassification || <MissingValue>Not set</MissingValue>}
                    </ParamValue>
                  </ParamRow>
                  <ParamRow>
                    <ParamLabel>Storeys Above Grade</ParamLabel>
                    <ParamValue>
                      {project?.storeysAboveGrade ?? <MissingValue>Not set</MissingValue>}
                    </ParamValue>
                  </ParamRow>
                  <ParamRow>
                    <ParamLabel>Building Footprint Area</ParamLabel>
                    <ParamValue>
                      {project?.buildingFootprintArea
                        ? `${project.buildingFootprintArea} ${project?.occupancyRegion === 'CA' ? 'm²' : 'ft²'}`
                        : <MissingValue>Not set</MissingValue>}
                    </ParamValue>
                  </ParamRow>
                  <ParamRow>
                    <ParamLabel>Project Type</ParamLabel>
                    <ParamValue>
                      {project?.type
                        ? (PROJECT_TYPE_LABELS[project.type] || project.type)
                        : <MissingValue>Not set</MissingValue>}
                    </ParamValue>
                  </ParamRow>
                </ParamGrid>
                {(!project?.storeysAboveGrade || !project?.buildingFootprintArea) && (
                  <MissingParamsNote>
                    ⚠ Some parameters are missing — adding more detail in Project Settings
                    will improve extraction accuracy.
                  </MissingParamsNote>
                )}
              </div>

              <div>
                <FieldLabel>
                  Construction Type <span style={{ color: '#e97024' }}>*</span>
                </FieldLabel>
                <FieldHint>
                  Showing {project?.occupancyRegion === 'CA' ? 'Canadian (NBC/OBC)' : 'US (IBC)'} construction types
                </FieldHint>
                <WizardSelect
                  value={wizardConstructionType}
                  onChange={e => setWizardConstructionType(e.target.value)}
                >
                  <option value="">— Select a construction type —</option>
                  {CONSTRUCTION_LIST.map(([key, title]) => (
                    <option key={key} value={key}>{title}</option>
                  ))}
                </WizardSelect>
                {wizardConstructionType && (
                  <WizardTypeDesc>
                    {CONSTRUCTION_LIST.find(([key]) => key === wizardConstructionType)?.[2]}
                  </WizardTypeDesc>
                )}
              </div>
            </WizardBody>

            <WizardFooter>
              <FooterHint>You're indexing a Building Code document</FooterHint>
              <div style={{ display: 'flex', gap: '0.625rem' }}>
                <WizardGhostBtn onClick={() => setWizardOpen(false)}>
                  Skip
                </WizardGhostBtn>
                <WizardPrimaryBtn
                  onClick={handleWizardConfirm}
                  disabled={!wizardConstructionType}
                >
                  Confirm &amp; Begin Indexing
                </WizardPrimaryBtn>
              </div>
            </WizardFooter>
          </WizardModal>
        </WizardOverlay>
      )}
      {zoningWizardOpen && (
        <WizardOverlay onClick={e => e.stopPropagation()}>
          <WizardModal>
            <WizardHeader>
              <WizardHeaderLeft>
                <WizardIconRing>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </WizardIconRing>
                <div>
                  <WizardTitle>Zoning Designation Recommended</WizardTitle>
                  <WizardSubtitle>One more parameter before indexing begins</WizardSubtitle>
                </div>
              </WizardHeaderLeft>
              <CloseBtn onClick={() => setZoningWizardOpen(false)} aria-label="Back to classifier">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </CloseBtn>
            </WizardHeader>

            <WizardBody>
              <MissingParamsNote>
                You're indexing a zoning bylaw, but this project has no zoning designation
                set. Adding it keeps extraction focused on the standards that apply to your
                zone — you can also skip and add it later in Project Settings.
              </MissingParamsNote>

              <div>
                <FieldLabel>Zoning Designation</FieldLabel>
                <FieldHint>The zone label from your bylaw or zoning map</FieldHint>
                <WizardSelect
                  as="input"
                  type="text"
                  value={zoningInput}
                  placeholder="e.g. CR 3.0, R2, MU-1"
                  onChange={e => setZoningInput(e.target.value)}
                />
              </div>
            </WizardBody>

            <WizardFooter>
              <FooterHint>You're indexing a Zoning bylaw</FooterHint>
              <div style={{ display: 'flex', gap: '0.625rem' }}>
                <WizardGhostBtn onClick={proceedAfterZoning}>
                  Skip
                </WizardGhostBtn>
                <WizardPrimaryBtn
                  onClick={proceedAfterZoning}
                  disabled={!zoningInput.trim()}
                >
                  Confirm &amp; Begin Indexing
                </WizardPrimaryBtn>
              </div>
            </WizardFooter>
          </WizardModal>
        </WizardOverlay>
      )}
    </Overlay>
  );
}